import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Trophy, Clock, Radio, CalendarDays, Sparkles, Image, ChevronLeft, ChevronRight, Loader2, Send, Download } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = { NS: 'Başlamadı', '1H': '1. Yarı', HT: 'Devre Arası', '2H': '2. Yarı', FT: 'Bitti', AET: 'Uzatma', PEN: 'Penaltı', PST: 'Ertelendi', CANC: 'İptal' };
const STATUS_COLOR = { NS: 'bg-blue-500/10 text-blue-400 border-blue-500/20', '1H': 'bg-red-500/10 text-red-400 border-red-500/20', HT: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', '2H': 'bg-red-500/10 text-red-400 border-red-500/20', FT: 'bg-green-500/10 text-green-400 border-green-500/20', AET: 'bg-green-500/10 text-green-400 border-green-500/20', PEN: 'bg-green-500/10 text-green-400 border-green-500/20' };

const CONTENT_TYPES = [
  { value: 'match_preview', label: 'Maç Önizleme', forStatus: ['NS'] },
  { value: 'match_start', label: 'Maç Duyurusu', forStatus: ['NS', '1H'] },
  { value: 'halftime_summary', label: 'Devre Arası Özeti', forStatus: ['HT', '2H', 'FT'] },
  { value: 'fulltime_summary', label: 'Maç Sonu Özeti', forStatus: ['FT', 'AET', 'PEN'] },
  { value: 'match_recap', label: 'Detaylı Özet', forStatus: ['FT', 'AET', 'PEN'] },
  { value: 'goal_alert', label: 'Gol Bildirimi', forStatus: ['1H', '2H', 'HT', 'FT'] },
];

export default function MatchCenterPage() {
  const [tab, setTab] = useState('today');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [fixtures, setFixtures] = useState([]);
  const [liveFixtures, setLiveFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showCountryPanel, setShowCountryPanel] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [fixtureDetail, setFixtureDetail] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedVisual, setGeneratedVisual] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState('match_start');
  const [selectedPlatform, setSelectedPlatform] = useState('telegram');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const loadFixtures = useCallback(() => {
    setLoading(true);
    api.get(`/sports/managed-fixtures?date=${selectedDate}`).then(r => { setFixtures(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [selectedDate]);

  const loadLive = () => api.get('/sports/live').then(r => setLiveFixtures(r.data)).catch(() => {});

  useEffect(() => { loadFixtures(); loadLive(); const i = setInterval(loadLive, 30000); return () => clearInterval(i); }, [loadFixtures]);

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  // Build 7 day buttons: -3 days to +3 days from today
  const dayButtons = [];
  const todayDate = new Date();
  for (let i = -3; i <= 3; i++) {
    const d = new Date(todayDate);
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().split('T')[0];
    const dayName = i === -3 ? '3 gün önce' : i === -2 ? 'Önceki gün' : i === -1 ? 'Dün' : i === 0 ? 'Bugün' : i === 1 ? 'Yarın' : i === 2 ? 'Sonraki gün' : '3 gün sonra';
    const shortDay = d.toLocaleDateString('tr-TR', { weekday: 'short' });
    const dayNum = d.getDate();
    const monthShort = d.toLocaleDateString('tr-TR', { month: 'short' });
    dayButtons.push({ iso, label: dayName, shortDay, dayNum, monthShort, isToday: i === 0, offset: i });
  }

  const openDetail = async (fixtureId) => {
    setSelectedFixture(fixtureId);
    setGeneratedContent(null); setGeneratedVisual(null);
    try { setFixtureDetail((await api.get(`/sports/fixtures/${fixtureId}`)).data); }
    catch { toast.error('Maç detayı yüklenemedi'); }
  };

  const handleGenerate = async () => {
    if (!selectedFixture) return;
    setGenerating(true); setGeneratedContent(null);
    try {
      const r = await api.post(`/sports/generate-content?fixture_id=${selectedFixture}&content_type=${selectedContentType}&platform=${selectedPlatform}`);
      setGeneratedContent(r.data);
      toast.success('İçerik üretildi');
    } catch (err) { toast.error(err.response?.data?.detail || 'İçerik üretimi başarısız'); }
    setGenerating(false);
  };

  const handleVisual = async () => {
    if (!selectedFixture) return;
    setGenerating(true); setGeneratedVisual(null);
    try {
      const r = await api.post(`/sports/generate-visual?fixture_id=${selectedFixture}&event_type=${selectedContentType}`);
      setGeneratedVisual(r.data);
      toast.success('Görsel oluşturuldu');
    } catch (err) { toast.error(err.response?.data?.detail || 'Görsel hatası'); }
    setGenerating(false);
  };

  const [sending, setSending] = useState(false);
  const [sendPlatforms, setSendPlatforms] = useState(['telegram']);

  const toggleSendPlatform = (p) => {
    setSendPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const handleQuickPost = async (withVisual = false) => {
    const content = generatedContent?.content || '';
    const imgUrl = generatedVisual?.image_url || null;
    if (!content && !imgUrl) { toast.error('Önce içerik veya görsel oluşturun'); return; }
    const caption = content || (fixtureDetail ? `${fixtureDetail.home.name} vs ${fixtureDetail.away.name} | ${fixtureDetail.league.name}` : 'Maç görseli');
    setSending(true);
    try {
      await api.post('/sports/social/post', {
        content: caption, platforms: sendPlatforms,
        image_url: withVisual ? imgUrl : null,
      });
      const names = sendPlatforms.map(p => p === 'telegram' ? 'Telegram' : p === 'twitter' ? 'X' : p === 'instagram' ? 'Instagram' : p).join(', ');
      toast.success(`${names} kanallarına gönderildi!`);
    } catch { toast.error('Gönderi hatası'); }
    setSending(false);
  };

  const isLive = (s) => ['1H', '2H', 'HT', 'ET', 'P'].includes(s);
  const isFinished = (s) => ['FT', 'AET', 'PEN'].includes(s);

  // Build country → league hierarchy from current fixtures
  const countryMap = {};
  fixtures.forEach(f => {
    const c = f.league.country || 'Diğer';
    const flag = f.league.flag || '';
    if (!countryMap[c]) countryMap[c] = { flag, leagues: {} };
    const lid = f.league.id;
    if (!countryMap[c].leagues[lid]) countryMap[c].leagues[lid] = { ...f.league, count: 0 };
    countryMap[c].leagues[lid].count++;
  });
  const sortedCountries = Object.entries(countryMap).sort((a, b) => {
    const aCount = Object.values(a[1].leagues).reduce((s, l) => s + l.count, 0);
    const bCount = Object.values(b[1].leagues).reduce((s, l) => s + l.count, 0);
    return bCount - aCount;
  });

  // Filter fixtures
  let displayFixtures = fixtures;
  if (selectedLeague) displayFixtures = fixtures.filter(f => f.league.id === selectedLeague);
  else if (selectedCountry) displayFixtures = fixtures.filter(f => (f.league.country || 'Diğer') === selectedCountry);

  // Group filtered fixtures by country → league
  const groupedByCountry = {};
  displayFixtures.forEach(f => {
    const c = f.league.country || 'Diğer';
    if (!groupedByCountry[c]) groupedByCountry[c] = { flag: f.league.flag, leagues: {} };
    const lid = f.league.id;
    if (!groupedByCountry[c].leagues[lid]) groupedByCountry[c].leagues[lid] = { ...f.league, fixtures: [] };
    groupedByCountry[c].leagues[lid].fixtures.push(f);
  });

  const renderFixture = (f) => (
    <div key={f.id} className="flex items-center p-3 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] hover:border-[#404040] transition-colors cursor-pointer group" onClick={() => openDetail(f.id)} data-testid={`fixture-${f.id}`}>
      <div className="flex items-center gap-2 flex-1 justify-end text-right">
        <span className="text-sm font-ibm text-white">{f.home.name}</span>
        <img src={f.home.logo} alt="" className="w-6 h-6 shrink-0" loading="lazy" />
      </div>
      <div className="w-28 text-center mx-3">
        {f.status === 'NS' ? (
          <span className="text-sm text-[#A3A3A3] font-jetbrains">{new Date(f.date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
        ) : (
          <div>
            <span className={`text-lg font-outfit font-bold ${isLive(f.status) ? 'text-red-400' : 'text-white'}`}>{f.goals.home ?? '-'} - {f.goals.away ?? '-'}</span>
            {isLive(f.status) && f.elapsed && <p className="text-xs text-red-400 font-jetbrains animate-pulse">{f.elapsed}'</p>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-1">
        <img src={f.away.logo} alt="" className="w-6 h-6 shrink-0" loading="lazy" />
        <span className="text-sm font-ibm text-white">{f.away.name}</span>
      </div>
      <Badge variant="outline" className={`ml-3 text-xs shrink-0 ${STATUS_COLOR[f.status] || 'text-[#525252] border-[#262626]'}`}>{isLive(f.status) ? 'CANLI' : STATUS_MAP[f.status] || f.status}</Badge>
    </div>
  );

  const renderGroupedFixtures = (fixtureList) => {
    const byCountry = {};
    fixtureList.forEach(f => {
      const c = f.league.country || 'Diğer';
      if (!byCountry[c]) byCountry[c] = { flag: f.league.flag, leagues: {} };
      const lid = f.league.id;
      if (!byCountry[c].leagues[lid]) byCountry[c].leagues[lid] = { ...f.league, fixtures: [] };
      byCountry[c].leagues[lid].fixtures.push(f);
    });
    if (Object.keys(byCountry).length === 0) return <div className="text-center py-12"><Trophy size={32} className="mx-auto text-[#262626] mb-3" /><p className="text-[#737373] font-ibm text-sm">Maç bulunamadı</p></div>;
    return Object.entries(byCountry).sort((a,b) => {
      const ac = Object.values(a[1].leagues).reduce((s,l) => s + l.fixtures.length, 0);
      const bc = Object.values(b[1].leagues).reduce((s,l) => s + l.fixtures.length, 0);
      return bc - ac;
    }).map(([country, data]) => (
      <div key={country} className="space-y-3">
        <div className="flex items-center gap-2 pt-2">
          {data.flag && <img src={data.flag} alt="" className="w-5 h-3.5 object-cover rounded-sm" />}
          <span className="text-sm font-outfit text-white font-medium">{country}</span>
          <span className="text-xs text-[#525252] font-ibm">{Object.values(data.leagues).reduce((s,l) => s + l.fixtures.length, 0)} maç</span>
        </div>
        {Object.values(data.leagues).map(league => (
          <div key={league.id} className="ml-2">
            <div className="flex items-center gap-2 mb-1.5 pl-2 border-l-2 border-[#262626]">
              <img src={league.logo} alt="" className="w-4 h-4" />
              <span className="text-xs font-ibm text-[#A3A3A3]">{league.name}</span>
              <span className="text-xs text-[#525252]">({league.fixtures.length})</span>
            </div>
            <div className="space-y-1 ml-2">{league.fixtures.map(renderFixture)}</div>
          </div>
        ))}
      </div>
    ));
  };

  const availableTypes = fixtureDetail ? CONTENT_TYPES.filter(ct => ct.forStatus.includes(fixtureDetail.status) || ct.forStatus.includes('NS')) : CONTENT_TYPES;

  return (
    <div data-testid="match-center-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Maç Merkezi</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">
            {liveFixtures.length > 0 && <span className="text-red-400 mr-2"><Radio size={12} className="inline mr-1 animate-pulse" />{liveFixtures.length} canlı</span>}
            {fixtures.length} maç
          </p>
        </div>
      </div>

      {/* Date Navigation - Day Buttons */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          <Button variant="ghost" onClick={() => changeDate(-1)} className="text-[#525252] hover:text-white h-9 w-9 p-0 shrink-0"><ChevronLeft size={16} /></Button>
          {dayButtons.map(db => (
            <button key={db.iso} onClick={() => setSelectedDate(db.iso)} data-testid={`day-${db.offset}`}
              className={`flex flex-col items-center px-3 py-2 rounded-lg min-w-[72px] shrink-0 transition-all duration-200 ${
                selectedDate === db.iso
                  ? 'bg-[#FACC15] text-black'
                  : db.isToday
                    ? 'bg-[#FACC15]/10 text-[#FACC15] border border-[#FACC15]/30'
                    : 'bg-[#141414] text-[#A3A3A3] border border-[#262626] hover:border-[#404040] hover:text-white'
              }`}>
              <span className="text-xs font-ibm font-medium">{db.label}</span>
              <span className={`text-lg font-outfit font-bold ${selectedDate === db.iso ? 'text-black' : ''}`}>{db.dayNum}</span>
              <span className="text-xs font-ibm opacity-70">{db.shortDay}</span>
            </button>
          ))}
          <Button variant="ghost" onClick={() => changeDate(1)} className="text-[#525252] hover:text-white h-9 w-9 p-0 shrink-0"><ChevronRight size={16} /></Button>
          <div className="shrink-0 ml-2">
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="bg-[#141414] border border-[#262626] text-white rounded px-3 py-2 text-xs font-jetbrains h-[60px]" data-testid="date-picker" />
          </div>
        </div>
        {/* Active date label */}
        <p className="text-xs text-[#525252] font-ibm">
          {new Date(selectedDate).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          {isToday && <span className="text-[#FACC15] ml-2">(Bugün)</span>}
        </p>
      </div>

      {/* Country / League Filter */}
      <div className="flex gap-6">
        {/* Country sidebar */}
        <div className="w-56 shrink-0">
          <div className="bg-[#141414] border border-[#262626] rounded-lg overflow-hidden">
            <div className="p-3 border-b border-[#262626]">
              <button onClick={() => { setSelectedCountry(null); setSelectedLeague(null); }} className={`w-full text-left px-2 py-1.5 rounded text-sm font-ibm ${!selectedCountry && !selectedLeague ? 'text-[#FACC15] font-medium' : 'text-[#A3A3A3] hover:text-white'}`} data-testid="filter-all">
                Tüm Ülkeler ({fixtures.length})
              </button>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {sortedCountries.map(([country, data]) => {
                const totalCount = Object.values(data.leagues).reduce((s, l) => s + l.count, 0);
                // Keep open if this country is selected OR a league under it is selected
                const hasSelectedLeague = selectedLeague && Object.keys(data.leagues).includes(String(selectedLeague));
                const isOpen = selectedCountry === country || hasSelectedLeague;
                return (
                  <div key={country}>
                    <button onClick={() => { setSelectedCountry(selectedCountry === country && !hasSelectedLeague ? null : country); if (!hasSelectedLeague) setSelectedLeague(null); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-[#1A1A1A] transition-colors ${isOpen ? 'bg-white/5 text-white' : selectedCountry === country ? 'bg-white/5 text-white' : 'text-[#A3A3A3] hover:bg-white/5 hover:text-white'}`}
                      data-testid={`country-${country}`}>
                      {data.flag && <img src={data.flag} alt="" className="w-4 h-3 object-cover rounded-sm shrink-0" />}
                      <span className="text-xs font-ibm flex-1 truncate">{country}</span>
                      <span className="text-xs text-[#525252] font-jetbrains">{totalCount}</span>
                    </button>
                    {isOpen && (
                      <div className="bg-[#0A0A0A]">
                        <button onClick={() => { setSelectedCountry(country); setSelectedLeague(null); }}
                          className={`w-full flex items-center gap-2 px-4 py-1.5 text-left transition-colors ${selectedCountry === country && !selectedLeague ? 'text-[#FACC15]' : 'text-[#525252] hover:text-white hover:bg-white/5'}`}>
                          <span className="text-xs font-ibm">Tüm Ligler</span>
                          <span className="text-xs text-[#525252] font-jetbrains">({totalCount})</span>
                        </button>
                        {Object.entries(data.leagues).map(([lid, league]) => (
                          <button key={lid} onClick={() => { setSelectedCountry(country); setSelectedLeague(selectedLeague === Number(lid) ? null : Number(lid)); }}
                            className={`w-full flex items-center gap-2 px-4 py-1.5 text-left transition-colors ${selectedLeague === Number(lid) ? 'bg-[#FACC15]/10 text-[#FACC15] border-l-2 border-[#FACC15]' : 'text-[#737373] hover:text-white hover:bg-white/5'}`}>
                            <img src={league.logo} alt="" className="w-3.5 h-3.5 shrink-0" />
                            <span className="text-xs font-ibm flex-1 truncate">{league.name}</span>
                            <span className="text-xs text-[#525252] font-jetbrains">{league.count}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Active filter display */}
          {(selectedCountry || selectedLeague) && (
            <div className="flex items-center gap-2 mb-3 p-2.5 bg-[#141414] border border-[#262626] rounded-lg">
              <span className="text-xs text-[#525252] font-ibm">Filtre:</span>
              {selectedCountry && (() => {
                const cd = countryMap[selectedCountry];
                return <div className="flex items-center gap-1.5"><img src={cd?.flag} alt="" className="w-4 h-3 object-cover rounded-sm" /><span className="text-sm text-white font-ibm font-medium">{selectedCountry}</span></div>;
              })()}
              {selectedLeague && (() => {
                const l = fixtures.find(f => f.league.id === selectedLeague)?.league;
                return l ? <><span className="text-[#525252]">/</span><img src={l.logo} alt="" className="w-4 h-4" /><span className="text-sm text-[#FACC15] font-ibm font-medium">{l.name}</span></> : null;
              })()}
              <span className="text-xs text-[#525252] font-ibm ml-1">({displayFixtures.length} maç)</span>
              <button onClick={() => { setSelectedCountry(null); setSelectedLeague(null); }} className="ml-auto text-xs text-[#525252] hover:text-red-400 font-ibm px-2 py-0.5 rounded hover:bg-red-500/10">Temizle</button>
            </div>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[#141414] border border-[#262626]">
              <TabsTrigger value="live" className="data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-sm"><Radio size={12} className="mr-1.5" /> Canlı ({liveFixtures.length})</TabsTrigger>
              <TabsTrigger value="today" className="data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-sm"><CalendarDays size={12} className="mr-1.5" /> Tümü ({displayFixtures.length})</TabsTrigger>
              <TabsTrigger value="upcoming" className="data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-sm"><Clock size={12} className="mr-1.5" /> Gelecek ({displayFixtures.filter(f => f.status === 'NS').length})</TabsTrigger>
              <TabsTrigger value="finished" className="data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-sm"><Trophy size={12} className="mr-1.5" /> Biten ({displayFixtures.filter(f => isFinished(f.status)).length})</TabsTrigger>
            </TabsList>

            <TabsContent value="live" className="mt-4 space-y-4">
              {liveFixtures.length === 0 ? <div className="text-center py-12"><Radio size={32} className="mx-auto text-[#262626] mb-3" /><p className="text-[#737373] font-ibm text-sm">Canlı maç yok</p></div>
              : renderGroupedFixtures(liveFixtures)}
            </TabsContent>

            <TabsContent value="today" className="mt-4 space-y-4">
              {loading ? <p className="text-[#A3A3A3] font-ibm">Yukleniyor...</p> : displayFixtures.length === 0 && fixtures.length === 0 ? (
                <div className="text-center py-12" data-testid="no-managed-leagues-msg">
                  <Trophy size={32} className="mx-auto text-[#262626] mb-3" />
                  <p className="text-[#737373] font-ibm text-sm">Bu tarih için maç bulunamadı</p>
                  <p className="text-[#525252] font-ibm text-xs mt-1">Spor Yönetimi sayfasından aktif lig eklediğinizden emin olun</p>
                </div>
              ) : renderGroupedFixtures(displayFixtures)}
            </TabsContent>

            <TabsContent value="upcoming" className="mt-4 space-y-4">
              {renderGroupedFixtures(displayFixtures.filter(f => f.status === 'NS'))}
            </TabsContent>

            <TabsContent value="finished" className="mt-4 space-y-4">
              {renderGroupedFixtures(displayFixtures.filter(f => isFinished(f.status)))}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Fixture Detail + Content Generation Modal */}
      <Dialog open={!!selectedFixture} onOpenChange={() => { setSelectedFixture(null); setFixtureDetail(null); setGeneratedContent(null); setGeneratedVisual(null); }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-outfit text-white">Maç Detayı</DialogTitle></DialogHeader>
          {fixtureDetail ? (
            <div className="space-y-5">
              {/* Score Header */}
              <div className="text-center p-6 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]">
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center"><img src={fixtureDetail.home.logo} alt="" className="w-14 h-14 mx-auto mb-2" /><p className="text-white font-ibm font-medium text-sm">{fixtureDetail.home.name}</p></div>
                  <div>
                    <p className={`text-4xl font-outfit font-bold ${isLive(fixtureDetail.status) ? 'text-red-400' : 'text-white'}`}>{fixtureDetail.goals.home ?? '-'} - {fixtureDetail.goals.away ?? '-'}</p>
                    <Badge variant="outline" className={`mt-1 ${STATUS_COLOR[fixtureDetail.status] || ''}`}>{STATUS_MAP[fixtureDetail.status] || fixtureDetail.status}</Badge>
                    {fixtureDetail.elapsed && <p className="text-xs text-red-400 mt-1">{fixtureDetail.elapsed}'</p>}
                  </div>
                  <div className="text-center"><img src={fixtureDetail.away.logo} alt="" className="w-14 h-14 mx-auto mb-2" /><p className="text-white font-ibm font-medium text-sm">{fixtureDetail.away.name}</p></div>
                </div>
                <div className="flex items-center justify-center gap-2 mt-3"><img src={fixtureDetail.league.logo} alt="" className="w-4 h-4" /><span className="text-xs text-[#A3A3A3] font-ibm">{fixtureDetail.league.name}</span><span className="text-xs text-[#525252]">|</span><span className="text-xs text-[#525252] font-ibm">{fixtureDetail.venue}</span></div>
                <p className="text-xs text-[#525252] font-jetbrains mt-1">{new Date(fixtureDetail.date).toLocaleString('tr-TR')}</p>
              </div>

              {/* Events */}
              {fixtureDetail.events?.length > 0 && (
                <div><h3 className="font-outfit text-sm text-white mb-2">Olaylar</h3>
                  <div className="space-y-1">{fixtureDetail.events.map((ev, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 bg-[#0A0A0A] rounded border border-[#1A1A1A] text-xs font-ibm">
                      <span className="text-[#FACC15] font-jetbrains w-8">{ev.time?.elapsed}'</span>
                      <span>{ev.type === 'Goal' ? '⚽' : ev.type === 'Card' ? (ev.detail === 'Yellow Card' ? '🟨' : '🟥') : ev.type === 'subst' ? '🔄' : '📋'}</span>
                      <span className="text-white">{ev.player?.name}</span>
                      <span className="text-[#525252]">{ev.team?.name}</span>
                    </div>))}
                  </div>
                </div>
              )}

              <Separator className="bg-[#262626]" />

              {/* AI Content Generation */}
              <div>
                <h3 className="font-outfit text-sm text-white mb-3 flex items-center gap-2"><Sparkles size={16} className="text-[#FACC15]" /> AI İçerik Oluştur</h3>
                <div className="flex gap-2 flex-wrap mb-3">
                  <Select value={selectedContentType} onValueChange={setSelectedContentType}>
                    <SelectTrigger className="w-48 bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">{availableTypes.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                    <SelectTrigger className="w-36 bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="telegram">Telegram</SelectItem>
                      <SelectItem value="twitter">Twitter</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="general">Genel</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleGenerate} disabled={generating} className="bg-white text-black hover:bg-[#E5E5E5] h-8 text-xs" data-testid="gen-content-btn">
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <><Sparkles size={12} className="mr-1" /> Metin Üret</>}
                  </Button>
                  <Button onClick={handleVisual} disabled={generating} variant="outline" className="border-[#FACC15]/30 text-[#FACC15] hover:bg-[#FACC15]/10 h-8 text-xs" data-testid="gen-visual-btn">
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <><Image size={12} className="mr-1" /> Görsel Üret</>}
                  </Button>
                </div>

                {/* Generated Content */}
                {generatedContent && (
                  <div className="space-y-3">
                    <div className="bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-[#525252] font-jetbrains">{generatedContent.provider}/{generatedContent.model}</span>
                      </div>
                      <pre className="text-sm text-white font-ibm whitespace-pre-wrap leading-relaxed">{generatedContent.content}</pre>
                    </div>
                    {generatedContent.fixture && (
                      <div className="flex items-center gap-3 text-xs text-[#525252] font-ibm">
                        <img src={generatedContent.fixture.home_logo} alt="" className="w-4 h-4" />
                        <span>{generatedContent.fixture.home} {generatedContent.fixture.score} {generatedContent.fixture.away}</span>
                        <img src={generatedContent.fixture.away_logo} alt="" className="w-4 h-4" />
                        {generatedContent.fixture.goals_detail && generatedContent.fixture.goals_detail !== 'Henüz gol yok' && <span>| Goller: {generatedContent.fixture.goals_detail}</span>}
                      </div>
                    )}
                  </div>
                )}

                {/* Generated Visual */}
                {generatedVisual && (
                  <div className="mt-3">
                    <img src={`${backendUrl}${generatedVisual.image_url || generatedVisual}`} alt="" className="w-full rounded-lg border border-[#262626]" />
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        {generatedVisual.fixture && <><img src={generatedVisual.fixture.home_logo} alt="" className="w-4 h-4" /><img src={generatedVisual.fixture.away_logo} alt="" className="w-4 h-4" /></>}
                      </div>
                      <a href={`${backendUrl}${generatedVisual.image_url || generatedVisual}`} download className="text-xs text-[#FACC15] hover:text-white font-ibm flex items-center gap-1"><Download size={12} /> İndir</a>
                    </div>
                  </div>
                )}

                {/* Platform Selection & Send */}
                {(generatedContent || generatedVisual) && (
                  <div className="mt-4 p-3 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]">
                    <p className="text-xs text-[#737373] font-ibm mb-2">Gönderilecek platformlar:</p>
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {[
                        { id: 'telegram', label: 'Telegram', color: '#0088CC' },
                        { id: 'twitter', label: 'X (Twitter)', color: '#1DA1F2' },
                        { id: 'instagram', label: 'Instagram', color: '#E4405F' },
                      ].map(p => (
                        <button key={p.id} onClick={() => toggleSendPlatform(p.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-ibm border transition-all ${
                            sendPlatforms.includes(p.id)
                              ? 'border-[#FACC15] bg-[#FACC15]/10 text-white'
                              : 'border-[#262626] bg-[#141414] text-[#525252] hover:border-[#404040]'
                          }`} data-testid={`toggle-platform-${p.id}`}>
                          <div className="w-3 h-3 rounded-full" style={{ background: sendPlatforms.includes(p.id) ? p.color : '#404040' }} />
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {generatedContent && (
                        <Button onClick={() => handleQuickPost(false)} disabled={sending || sendPlatforms.length === 0} variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white h-8 text-xs flex-1" data-testid="send-text-btn">
                          {sending ? <Loader2 size={12} className="animate-spin" /> : <><Send size={12} className="mr-1" /> Metin Gönder</>}
                        </Button>
                      )}
                      {generatedVisual && (
                        <Button onClick={() => handleQuickPost(true)} disabled={sending || sendPlatforms.length === 0} className="bg-[#0088CC] text-white hover:bg-[#0077B5] h-8 text-xs flex-1" data-testid="send-visual-btn">
                          {sending ? <Loader2 size={12} className="animate-spin" /> : <><Send size={12} className="mr-1" /> Görsel + Metin Gönder</>}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : <p className="text-[#A3A3A3] font-ibm text-center py-8">Yükleniyor...</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
